/**
 * OperatorHome — the primary first-time and returning-merchant screen.


 * Card layout order:
 *   1. Next Action (if present) — most prominent
 *   2. Needs Attention / Blockers (if present)
 *   3. Publish / Preview status
 *   4. Setup progress (only while incomplete)
 *   5. Diagnostics (last, only when attention is needed)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button, LinearProgress } from '@material-ui/core';
import {
  CheckCircle,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  HelpOutline,
  FiberManualRecord,
} from '@material-ui/icons';
import clsx from 'clsx';
import { formatLastSyncTimeLocalized } from '../../../catalog-sync/catalogSyncUtils';
import {
  ConnectionStatus,
  IndexingStatus,
  StorefrontMode,
  PreviewBuildStatus,
  PreviewBrowserStatus,
  LiveStatus,
  PublishState,
  IntegrationHealth,
  DiagnosticsSeverity,
  NextActionKey,
  ActionRoute,
  MODE_SUPPORT,
} from '../../operatorState';
import { resolveActionRoute } from '../../resolveActionRoute';
import { deriveChecklistSteps } from '../../deriveChecklistSteps';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import { usePreviewOpener } from '../../../shell/preview/usePreviewOpener';
import OnboardingChecklist from '../OnboardingChecklist';
import CatalogSyncControlCard from '../CatalogSyncControlCard';
import AnalyticsSummaryBlock from '../AnalyticsSummaryBlock';
import styles from './OperatorHome.module.scss';
// Merchant-facing status text map (i18n keys)
// ---------------------------------------------------------------------------
const STATUS_MESSAGE_KEY = {
  connection: {
    [ConnectionStatus.NOT_CONNECTED]: 'operator.home.connection.not_connected',
    [ConnectionStatus.CONNECTED]: 'operator.home.connection.connected',
    [ConnectionStatus.ERROR]: 'operator.home.connection.error',
  },
  indexing: {
    [IndexingStatus.IDLE]: 'operator.home.indexing.idle',
    [IndexingStatus.RUNNING]: 'operator.home.indexing.running',
    [IndexingStatus.READY]: 'operator.home.indexing.ready',
    [IndexingStatus.ERROR]: 'operator.home.indexing.error',
  },
  mode: {
    [StorefrontMode.WIDGET]: 'operator.home.mode.widget',
    [StorefrontMode.CLASSIC]: 'operator.home.mode.classic',
    [StorefrontMode.COMBINED]: 'operator.home.mode.combined',
  },
  previewBuild: {
    [PreviewBuildStatus.UNKNOWN]: 'operator.home.preview_build.unknown',
    [PreviewBuildStatus.NOT_READY]: 'operator.home.preview_build.not_ready',
    [PreviewBuildStatus.BUILDING]: 'operator.home.preview_build.building',
    [PreviewBuildStatus.READY]: 'operator.home.preview_build.ready',
    [PreviewBuildStatus.ERROR]: 'operator.home.preview_build.error',
  },
  previewBrowser: {
    [PreviewBrowserStatus.UNKNOWN]: 'operator.home.preview_browser.unknown',
    [PreviewBrowserStatus.DISABLED]: 'operator.home.preview_browser.disabled',
    [PreviewBrowserStatus.ENABLED]: 'operator.home.preview_browser.enabled',
    [PreviewBrowserStatus.STALE]: 'operator.home.preview_browser.stale',
    [PreviewBrowserStatus.ERROR]: 'operator.home.preview_browser.error',
  },
  live: {
    [LiveStatus.UNKNOWN]: 'operator.home.live.unknown',
    [LiveStatus.NOT_ACTIVE]: 'operator.home.live.not_active',
    [LiveStatus.ACTIVE]: 'operator.home.live.active',
    [LiveStatus.ERROR]: 'operator.home.live.error',
  },
  integrationHealth: {
    [IntegrationHealth.HEALTHY]: 'operator.home.integration.healthy',
    [IntegrationHealth.WARNING]: 'operator.home.integration.warning',
    [IntegrationHealth.ERROR]: 'operator.home.integration.error',
    [IntegrationHealth.UNKNOWN]: 'operator.home.integration.unknown',
  },
};
// Severity helpers
// ---------------------------------------------------------------------------
function severityIcon(severity) {
  switch (severity) {
    case 'error': return <ErrorIcon fontSize="inherit" />;
    case 'warning': return <WarningIcon fontSize="inherit" />;
    case 'info': return <InfoIcon fontSize="inherit" />;
    default: return <CheckCircle fontSize="inherit" />;
  }
}

function severityIconClass(severity) {
  switch (severity) {
    case 'error': return styles.iconErr;
    case 'warning': return styles.iconWarn;
    case 'info': return styles.iconInfo;
    default: return styles.iconOk;
  }
}

function fieldSeverity(field, value) {
  const errorValues = {
    connection: [ConnectionStatus.ERROR],
    indexing: [IndexingStatus.ERROR],
    previewBuild: [PreviewBuildStatus.ERROR],
    previewBrowser: [PreviewBrowserStatus.ERROR],
    live: [LiveStatus.ERROR],
    integrationHealth: [IntegrationHealth.ERROR],
  };
  const warningValues = {
    previewBrowser: [PreviewBrowserStatus.STALE],
    integrationHealth: [IntegrationHealth.WARNING],
  };
  if (errorValues[field]?.includes(value)) return 'error';
  if (warningValues[field]?.includes(value)) return 'warning';
  return null;
}

function cardSeverityClass(severity) {
  if (severity === 'error') return styles.cardError;
  if (severity === 'warning') return styles.cardWarning;
  return '';
}

function statusSeverity(field, value) {
  const okValues = {
    connection: [ConnectionStatus.CONNECTED],
    indexing: [IndexingStatus.READY],
    previewBuild: [PreviewBuildStatus.READY],
    previewBrowser: [PreviewBrowserStatus.ENABLED],
    live: [LiveStatus.ACTIVE],
    integrationHealth: [IntegrationHealth.HEALTHY],
  };
  if (okValues[field]?.includes(value)) return 'ok';
  return fieldSeverity(field, value) || 'neutral';
}

function statusIconClass(severity) {
  switch (severity) {
    case 'ok': return styles.iconOk;
    case 'error': return styles.iconErr;
    case 'warning': return styles.iconWarn;
    default: return styles.iconNeutral;
  }
}

function statusIcon(severity) {
  switch (severity) {
    case 'ok': return <CheckCircle fontSize="inherit" />;
    case 'error': return <ErrorIcon fontSize="inherit" />;
    case 'warning': return <WarningIcon fontSize="inherit" />;
    default: return <FiberManualRecord fontSize="inherit" />;
  }
}
// Individual cards
// ---------------------------------------------------------------------------
function StatusCard({ title, field, value, localizeMessage, hint, action, isLoading, showProgress }) {
  if (isLoading) {
    return <div className={styles.skeleton} data-testid={`card-${field}-loading`} />;
  }
  const sev = fieldSeverity(field, value);
  const stSev = statusSeverity(field, value);
  const messageKey = STATUS_MESSAGE_KEY[field]?.[value];
  const label = messageKey ? localizeMessage(messageKey) : String(value ?? '—');

  return (
    <div
      className={clsx(styles.card, cardSeverityClass(sev))}
      data-testid={`card-${field}`}
    >
      <div className={clsx(styles.cardIcon, statusIconClass(stSev))}>
        {statusIcon(stSev)}
      </div>
      <div className={styles.cardBody}>
        <p className={styles.cardTitle}>{title}</p>
        <p className={styles.cardValue}>{label}</p>
        {hint && <p className={styles.cardHint}>{hint}</p>}
        {showProgress && <LinearProgress />}
      </div>
      {action && (
        <div className={styles.cardAction}>
          {action}
        </div>
      )}
    </div>
  );
}

StatusCard.propTypes = {
  title: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  value: PropTypes.string,
  localizeMessage: PropTypes.func.isRequired,
  hint: PropTypes.string,
  action: PropTypes.node,
  isLoading: PropTypes.bool,
  showProgress: PropTypes.bool,
};

StatusCard.defaultProps = {
  value: null,
  hint: null,
  action: null,
  isLoading: false,
  showProgress: false,
};
// IndexingCard — live-aware Catalog Readiness card
// ---------------------------------------------------------------------------
function IndexingCard({
  localizeMessage,
  liveIndexing,
  onStartIndexing,
  indexingFallbackValue,
  isLoading,
}) {
  if (isLoading) {
    return <div className={styles.skeleton} data-testid="card-indexing-loading" />;
  }
  if (!liveIndexing) {
    return (
      <StatusCard
        title={localizeMessage('operator.home.indexing.title')}
        field="indexing"
        value={indexingFallbackValue}
        localizeMessage={localizeMessage}
      />
    );
  }

  const { status, progress, activeIndexPresent, error: liveError, isStarting, lastSuccessAt, indexedCount, totalCount } = liveIndexing;

  const lastUpdatedHint = lastSuccessAt
    ? `${localizeMessage('operator.home.indexing.last_updated')} ${formatLastSyncTimeLocalized(lastSuccessAt, localizeMessage)}`
    : null;

  if (status === IndexingStatus.ERROR) {
    return (
      <div className={clsx(styles.card, styles.cardError)} data-testid="card-indexing">
        <div className={clsx(styles.cardIcon, styles.iconErr)}>
          <ErrorIcon fontSize="inherit" />
        </div>
        <div className={styles.cardBody}>
          <p className={styles.cardTitle}>{localizeMessage('operator.home.indexing.title')}</p>
          <p className={styles.cardValue}>{localizeMessage('operator.home.indexing.error')}</p>
          {liveError && <p className={styles.cardHint}>{liveError}</p>}
        </div>
        {onStartIndexing && (
          <div className={styles.cardAction}>
            <Button
              size="small"
              variant="outlined"
              onClick={onStartIndexing}
              disabled={isStarting}
              data-testid="retry-indexing-btn"
            >
              {localizeMessage('operator.home.indexing.cta_retry')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (status === IndexingStatus.RUNNING) {
    return (
      <div className={styles.card} data-testid="card-indexing">
        <div className={clsx(styles.cardIcon, styles.iconNeutral)}>
          <FiberManualRecord fontSize="inherit" />
        </div>
        <div className={styles.cardBody}>
          <p className={styles.cardTitle}>{localizeMessage('operator.home.indexing.title')}</p>
          <p className={styles.cardValue}>{localizeMessage('operator.home.indexing.running')}</p>
          <p className={styles.cardHint}>
            {totalCount > 0
              ? `${indexedCount.toLocaleString()} / ${totalCount.toLocaleString()} ${
                localizeMessage('operator.home.indexing.progress_hint')
              }`
              : localizeMessage('operator.home.indexing.progressPercent', { progress })}
          </p>
          <LinearProgress variant="determinate" value={progress} data-testid="indexing-progress-bar" />
        </div>
      </div>
    );
  }
  if (status === IndexingStatus.IDLE && !activeIndexPresent) {
    const action = onStartIndexing ? (
      <Button
        size="small"
        variant="outlined"
        onClick={onStartIndexing}
        disabled={isStarting}
        data-testid="start-indexing-btn"
      >
        {localizeMessage('operator.home.indexing.cta_start')}
      </Button>
    ) : null;

    return (
      <StatusCard
        title={localizeMessage('operator.home.indexing.title')}
        field="indexing"
        value={status}
        localizeMessage={localizeMessage}
        action={action}
      />
    );
  }
  const catalogActionBtn = onStartIndexing ? (
    <Button
      size="small"
      variant="outlined"
      onClick={onStartIndexing}
      disabled={isStarting}
      data-testid="update-catalog-btn"
    >
      {localizeMessage('operator.home.indexing.cta_update')}
    </Button>
  ) : null;
  return (
    <StatusCard
      title={localizeMessage('operator.home.indexing.title')}
      field="indexing"
      value={status}
      localizeMessage={localizeMessage}
      hint={lastUpdatedHint}
      action={catalogActionBtn}
    />
  );
}

IndexingCard.propTypes = {
  localizeMessage: PropTypes.func.isRequired,
  liveIndexing: PropTypes.shape({
    status: PropTypes.string.isRequired,
    progress: PropTypes.number.isRequired,
    activeIndexPresent: PropTypes.bool.isRequired,
    error: PropTypes.string,
    isStarting: PropTypes.bool,
    lastSuccessAt: PropTypes.string,
    startedAt: PropTypes.string,
    finishedAt: PropTypes.string,
    indexedCount: PropTypes.number,
    totalCount: PropTypes.number,
  }),
  onStartIndexing: PropTypes.func,
  indexingFallbackValue: PropTypes.string,
  isLoading: PropTypes.bool,
};

IndexingCard.defaultProps = {
  liveIndexing: null,
  onStartIndexing: null,
  indexingFallbackValue: null,
  isLoading: false,
};
// Next Action Banner
// ---------------------------------------------------------------------------
function NextActionBanner({ nextAction, shopId, localizeMessage, onInlineAction }) {
  const history = useHistory();

  if (!nextAction) {
    return null;
  }

  const label = localizeMessage(nextAction.messageKey);
  const path = resolveActionRoute(nextAction.route, shopId);
  const bannerClass = clsx(
    styles.nextAction,
    nextAction.severity === 'error' && styles.nextActionError,
    nextAction.severity === 'warning' && styles.nextActionWarning,
  );

  const handleCta = path
    ? () => history.push(path)
    : onInlineAction || null;

  return (
    <div className={bannerClass} data-testid="next-action">
      <div className={clsx(styles.cardIcon, severityIconClass(nextAction.severity))}>
        {severityIcon(nextAction.severity)}
      </div>
      <div className={styles.nextActionBody}>
        <p className={styles.nextActionLabel}>
          {localizeMessage('operator.home.next_step')}
        </p>
        <p className={styles.nextActionText}>{label}</p>
      </div>
      {handleCta && (
        <div className={styles.nextActionCta}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleCta}
            data-testid="next-action-cta"
          >
            {label}
          </Button>
        </div>
      )}
    </div>
  );
}

NextActionBanner.propTypes = {
  nextAction: PropTypes.shape({
    key: PropTypes.string.isRequired,
    messageKey: PropTypes.string.isRequired,
    route: PropTypes.string,
    severity: PropTypes.string.isRequired,
  }),
  shopId: PropTypes.string,
  localizeMessage: PropTypes.func.isRequired,
  onInlineAction: PropTypes.func,
};

NextActionBanner.defaultProps = {
  nextAction: null,
  shopId: null,
  onInlineAction: null,
};
// Attention Banner
// ---------------------------------------------------------------------------
function AttentionBanner({ diagnosticsSeverity, localizeMessage, shopId }) {
  const history = useHistory();
  if (
    diagnosticsSeverity !== DiagnosticsSeverity.WARNING
    && diagnosticsSeverity !== DiagnosticsSeverity.ERROR
  ) {
    return null;
  }
  const isError = diagnosticsSeverity === DiagnosticsSeverity.ERROR;
  return (
    <div
      className={clsx(styles.attention, isError && styles.attentionError)}
      data-testid="attention-banner"
    >
      {isError
        ? <ErrorIcon color="error" />
        : <WarningIcon style={{ color: '#FFC453' }} />}
      <span className={styles.attentionText}>
        {localizeMessage(
          isError
            ? 'operator.home.attention.error'
            : 'operator.home.attention.warning',
        )}
      </span>
      <Button
        size="small"
        variant="outlined"
        onClick={() => history.push(resolveActionRoute(ActionRoute.DIAGNOSTICS, shopId))}
      >
        {localizeMessage('operator.home.view_details')}
      </Button>
    </div>
  );
}

AttentionBanner.propTypes = {
  diagnosticsSeverity: PropTypes.string.isRequired,
  localizeMessage: PropTypes.func.isRequired,
  shopId: PropTypes.string,
};

AttentionBanner.defaultProps = {
  shopId: null,
};
// Plugin Attention Banner — shown when plugin-side diagnostics need attention
// ---------------------------------------------------------------------------
function PluginAttentionBanner({ summary, capabilities, localizeMessage, shopId }) {
  const history = useHistory();
  if (!capabilities?.diagnosticEventsV1 || !summary?.needsAttention) {
    return null;
  }
  const isError = summary.highestSeverity === 'error' || summary.highestSeverity === 'critical';
  return (
    <div
      className={clsx(styles.attention, isError && styles.attentionError)}
      data-testid="plugin-attention-banner"
    >
      {isError
        ? <ErrorIcon color="error" />
        : <WarningIcon style={{ color: '#FFC453' }} />}
      <span className={styles.attentionText}>
        {localizeMessage(
          isError
            ? 'operator.home.plugin_attention.error'
            : 'operator.home.plugin_attention.warning',
        )}
      </span>
      <Button
        size="small"
        variant="outlined"
        onClick={() => history.push(resolveActionRoute(ActionRoute.DIAGNOSTICS, shopId))}
        data-testid="plugin-attention-cta"
      >
        {localizeMessage('operator.home.view_details')}
      </Button>
    </div>
  );
}

PluginAttentionBanner.propTypes = {
  summary: PropTypes.shape({
    highestSeverity: PropTypes.string,
    needsAttention: PropTypes.bool,
  }),
  capabilities: PropTypes.shape({
    diagnosticEventsV1: PropTypes.bool,
  }),
  localizeMessage: PropTypes.func.isRequired,
  shopId: PropTypes.string,
};

PluginAttentionBanner.defaultProps = {
  summary: null,
  capabilities: null,
  shopId: null,
};
// Diagnostics Entry — card style, always visible
// ---------------------------------------------------------------------------
function DiagnosticsEntry({ diagnosticsSeverity, localizeMessage, shopId }) {
  const history = useHistory();
  const hasBadge = diagnosticsSeverity === DiagnosticsSeverity.WARNING
    || diagnosticsSeverity === DiagnosticsSeverity.ERROR;
  return (
    <div className={clsx(styles.card)} data-testid="diagnostics-entry">
      <div className={clsx(styles.cardIcon, styles.iconNeutral)}>
        <HelpOutline fontSize="inherit" />
      </div>
      <div className={styles.cardBody}>
        <p className={styles.cardTitle}>{localizeMessage('operator.home.diagnostics.title')}</p>
        <p className={styles.cardHint}>{localizeMessage('operator.home.diagnostics.hint')}</p>
      </div>
      <div className={styles.cardAction}>
        <Button
          variant="text"
          color="default"
          size="small"
          onClick={() => history.push(resolveActionRoute(ActionRoute.DIAGNOSTICS, shopId))}
        >
          {localizeMessage('operator.home.diagnostics')}
        </Button>
        {hasBadge && (
          <span
            className={clsx(
              styles.diagBadge,
              diagnosticsSeverity === DiagnosticsSeverity.ERROR
                ? styles.badgeErr
                : styles.badgeWarn,
            )}
            data-testid="diagnostics-badge"
          >
            !
          </span>
        )}
      </div>
    </div>
  );
}

DiagnosticsEntry.propTypes = {
  diagnosticsSeverity: PropTypes.string.isRequired,
  localizeMessage: PropTypes.func.isRequired,
  shopId: PropTypes.string,
};

DiagnosticsEntry.defaultProps = {
  shopId: null,
};
// PublishStatusCard — pre-live, disabled-live, unpublished, error, and building states
// ---------------------------------------------------------------------------
function PublishStatusCard({
  publishState,
  live,
  localizeMessage,
  shopId,
  onPublish,
  onRetry,
  onOpenPreview,
  canOpenPreview,
  isPublishing,
}) {
  // building
  if (publishState === PublishState.BUILDING) {
    return (
      <div className={clsx(styles.card, styles.publishCard)} data-testid="publish-status-card">
        <div className={styles.publishContent} data-testid="publish-status-sub-c">
          <p className={styles.publishTitle}>
            {localizeMessage('operator.home.publish.building.title')}
          </p>
          <p className={styles.publishHint}>
            {localizeMessage('operator.home.publish.building.hint')}
          </p>
          <LinearProgress data-testid="publish-progress-bar" />
        </div>
      </div>
    );
  }
  if (publishState === PublishState.ERROR) {
    return (
      <div className={clsx(styles.card, styles.publishCard, styles.cardError)} data-testid="publish-status-card">
        <div className={styles.publishContent} data-testid="publish-status-sub-d">
          <div className={styles.publishBody}>
            <p className={styles.publishTitle}>
              {localizeMessage('operator.home.publish.error.title')}
            </p>
            <p className={styles.publishHint}>
              {localizeMessage('operator.home.publish.error.hint')}
            </p>
          </div>
          <div className={styles.publishActions}>
            <Button
              size="medium"
              variant="contained"
              color="primary"
              onClick={onRetry}
              disabled={isPublishing || !onRetry}
              data-testid="publish-retry-btn"
            >
              {localizeMessage('operator.home.publish.error.cta')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (live === LiveStatus.ACTIVE && publishState === PublishState.UNPUBLISHED_CHANGES) {
    return (
      <div className={clsx(styles.card, styles.publishCard, styles.cardWarning)} data-testid="publish-status-card">
        <div className={styles.publishContent} data-testid="publish-status-unpublished">
          <div className={styles.publishBody}>
            <p className={styles.publishTitle}>
              {localizeMessage('operator.home.publish.unpublished.title')}
            </p>
            <p className={styles.publishHint}>
              {localizeMessage('operator.home.publish.unpublished.hint')}
            </p>
          </div>
          <div className={styles.publishActions}>
            <Button
              size="medium"
              variant="contained"
              color="primary"
              onClick={onPublish}
              disabled={isPublishing || !onPublish}
              data-testid="publish-unpublished-btn"
            >
              {localizeMessage('operator.home.publish.unpublished.cta')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  const liveIsOffForPublishing = live === LiveStatus.NOT_ACTIVE
    || (live !== LiveStatus.ACTIVE && publishState === PublishState.NOT_PUBLISHED);
  if (!liveIsOffForPublishing) return null;
  if (
    publishState === PublishState.PUBLISHED
    || publishState === PublishState.UNPUBLISHED_CHANGES
  ) {
    return (
      <div className={clsx(styles.card, styles.publishCard)} data-testid="publish-status-card">
        <div className={styles.publishContent} data-testid="publish-status-sub-b">
          <div className={styles.publishBody}>
            <p className={styles.publishTitle}>
              {localizeMessage('operator.home.publish.sub_b.title')}
            </p>
            <p className={styles.publishHint}>
              {localizeMessage('operator.home.publish.sub_b.hint')}
            </p>
          </div>
          <div className={styles.publishActions}>
            <Button
              size="medium"
              variant="contained"
              color="primary"
              onClick={onPublish}
              disabled={isPublishing || !onPublish}
              data-testid="publish-reenable-btn"
            >
              {localizeMessage('operator.home.publish.sub_b.cta')}
            </Button>
            <Button
              size="medium"
              variant="outlined"
              onClick={onOpenPreview}
              disabled={!canOpenPreview}
              data-testid="publish-sub-b-preview-btn"
            >
              {localizeMessage('operator.home.publish.preview_cta')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={clsx(styles.card, styles.publishCard)} data-testid="publish-status-card">
      <div className={styles.publishContent} data-testid="publish-status-sub-a">
        <div className={styles.publishBody}>
          <p className={styles.publishTitle}>
            {localizeMessage('operator.home.publish.sub_a.title')}
          </p>
          <p className={styles.publishHint}>
            {localizeMessage('operator.home.publish.sub_a.hint')}
          </p>
        </div>
        <div className={styles.publishActions}>
          <Button
            size="medium"
            variant="contained"
            color="primary"
            onClick={onPublish}
            disabled={isPublishing || !onPublish}
            data-testid="publish-btn"
          >
            {localizeMessage('operator.home.publish.sub_a.cta')}
          </Button>
          <Button
            size="medium"
            variant="outlined"
            color="primary"
            onClick={onOpenPreview}
            disabled={!canOpenPreview}
            data-testid="publish-sub-a-preview-btn"
          >
            {localizeMessage('operator.home.publish.preview_cta')}
          </Button>
        </div>
      </div>
    </div>
  );
}

PublishStatusCard.propTypes = {
  publishState: PropTypes.string,
  live: PropTypes.string.isRequired,
  localizeMessage: PropTypes.func.isRequired,
  shopId: PropTypes.string,
  onPublish: PropTypes.func,
  onRetry: PropTypes.func,
  onOpenPreview: PropTypes.func.isRequired,
  canOpenPreview: PropTypes.bool,
  isPublishing: PropTypes.bool,
};

PublishStatusCard.defaultProps = {
  publishState: null,
  shopId: null,
  canOpenPreview: false,
  isPublishing: false,
  onPublish: null,
  onRetry: null,
};
// Mode card helpers
// ---------------------------------------------------------------------------
function modeHint(mode, localizeMessage) {
  if (!mode) return localizeMessage('operator.home.mode.hint_choose');
  if (MODE_SUPPORT[mode]?.supported === false) {
    return localizeMessage('operator.home.mode.hint_unsupported');
  }
  return null;
}
// Main component
// ---------------------------------------------------------------------------
export default function OperatorHome({
  operatorState,
  shopId,
  isLoading,
  // eslint-disable-next-line no-unused-vars
  isServiceUpdating,
  liveIndexing,
  onStartIndexing,
  catalogSync,
  pluginDiagnosticsSummary,
  pluginCapabilities,
  onPublish,
  trialChip = null,
}) {
  const localizeMessage = useLocalizedMessage();
  const history = useHistory();
  const { previewShopAddress, openStorefrontPreview } = usePreviewOpener();
  const s = operatorState;
  const checklist = deriveChecklistSteps(s);
  const setupReadyForPreview = checklist.steps
    .filter((step) => step.id !== 'go_live')
    .every((step) => step.complete);

  // activeIndexPresent=true means the index is live and search is serving.
  const effectivePreviewBuild = (() => {
    if (!liveIndexing) return s.previewBuild;
    if (liveIndexing.activeIndexPresent) return PreviewBuildStatus.READY;
    if (liveIndexing.status === IndexingStatus.RUNNING) return PreviewBuildStatus.BUILDING;
    return PreviewBuildStatus.NOT_READY;
  })();

  const goWizard = () => history.push(`/app/${shopId}/setup`);

  const showDiagnosticsEntry = s.diagnosticsSeverity === DiagnosticsSeverity.WARNING
    || s.diagnosticsSeverity === DiagnosticsSeverity.ERROR
    || pluginDiagnosticsSummary?.needsAttention === true;
  const hasPublishedBundle = s.publishState === PublishState.PUBLISHED
    || s.publishState === PublishState.UNPUBLISHED_CHANGES;
  const liveIsWorking = s.live === LiveStatus.ACTIVE
    || (
      hasPublishedBundle
      && s.live !== LiveStatus.NOT_ACTIVE
      && s.live !== LiveStatus.ERROR
    );
  const liveForHome = liveIsWorking ? LiveStatus.ACTIVE : s.live;
  const firstRunPublishFlowOwnsCta = setupReadyForPreview && !liveIsWorking;
  const suppressNextAction = (
    (s.onboarding?.wizardCompleted === true || setupReadyForPreview)
    && s.nextAction?.key === NextActionKey.CONNECT_STORE
  ) || (
    firstRunPublishFlowOwnsCta
    && (
      s.nextAction?.key === NextActionKey.PUBLISH
      || s.nextAction?.key === NextActionKey.REFRESH_PREVIEW
    )
  );
  const effectiveNextAction = suppressNextAction ? null : s.nextAction;
  const showAnalyticsSummary = !isLoading && liveIsWorking;
  const showNextActionArea = isLoading || effectiveNextAction;
  const showAttentionBanner = s.diagnosticsSeverity === DiagnosticsSeverity.ERROR;
  const showSetupProgress = !checklist.isSetupComplete
    && s.onboarding?.wizardCompleted !== true
    && !liveIsWorking;
  const canPublishInitialBundle = setupReadyForPreview
    && !liveIsWorking
    && s.publishState === PublishState.NOT_PUBLISHED
    && (
      effectivePreviewBuild === PreviewBuildStatus.READY
      || s.preview === PreviewBuildStatus.READY
      || s.onboarding?.previewBuilt === true
    );

  const handleOpenPreview = () => {
    if (previewShopAddress) openStorefrontPreview();
  };
  const handlePublish = typeof onPublish === 'function' ? onPublish : null;
  const INLINE_ACTIONS = {
    [NextActionKey.GO_LIVE]: handlePublish,
    [NextActionKey.PUBLISH]: handlePublish,
    [NextActionKey.REFRESH_PREVIEW]: previewShopAddress ? handleOpenPreview : null,
  };
  const nextActionInlineHandler = effectiveNextAction
    ? INLINE_ACTIONS[effectiveNextAction.key] || null
    : null;
  return (
    <div className={styles.home} data-testid="operator-home">
      {trialChip && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          {trialChip}
        </div>
      )}

      {/* 1. Next Action */}
      {showNextActionArea && (isLoading ? (
        <div className={styles.skeleton} style={{ height: 80 }} data-testid="next-action-loading" />
      ) : (
        <NextActionBanner
          nextAction={effectiveNextAction}
          shopId={shopId}
          localizeMessage={localizeMessage}
          onInlineAction={nextActionInlineHandler}
        />
      ))}

      {/* 2. Publish status is the highest-priority working-mode warning. */}
      {!isLoading && (
        liveForHome === LiveStatus.NOT_ACTIVE
        || canPublishInitialBundle
        || (liveForHome === LiveStatus.ACTIVE && s.publishState === PublishState.UNPUBLISHED_CHANGES)
        || s.publishState === PublishState.BUILDING
        || s.publishState === PublishState.ERROR
      ) && (
        <PublishStatusCard
          publishState={s.publishState}
          live={liveForHome}
          localizeMessage={localizeMessage}
          shopId={shopId}
          onPublish={handlePublish}
          onRetry={handlePublish}
          onOpenPreview={handleOpenPreview}
          canOpenPreview={!!previewShopAddress}
          isPublishing={s.publishState === PublishState.BUILDING}
        />
      )}

      {showAnalyticsSummary && (
        <AnalyticsSummaryBlock
          shopId={shopId}
          localizeMessage={localizeMessage}
        />
      )}

      {/* 3. Needs Attention */}
      {!isLoading && showAttentionBanner && (
        <AttentionBanner
          diagnosticsSeverity={s.diagnosticsSeverity}
          localizeMessage={localizeMessage}
          shopId={shopId}
        />
      )}

      {/* 3a. Plugin-side diagnostics attention */}
      {!isLoading && (
        <PluginAttentionBanner
          summary={pluginDiagnosticsSummary}
          capabilities={pluginCapabilities}
          localizeMessage={localizeMessage}
          shopId={shopId}
        />
      )}

      {showSetupProgress && (
        <OnboardingChecklist
          checklist={checklist}
          shopId={shopId}
          localizeMessage={localizeMessage}
          isLoading={isLoading}
          onActivate={handlePublish}
          showPreviewAction={setupReadyForPreview && !liveIsWorking}
          canOpenPreview={!!previewShopAddress}
          onOpenPreview={handleOpenPreview}
        />
      )}

      {/* Diagnostics entry is not a normal Home card; show it only on attention. */}
      {showDiagnosticsEntry && (
        <DiagnosticsEntry
          diagnosticsSeverity={s.diagnosticsSeverity}
          localizeMessage={localizeMessage}
          shopId={shopId}
        />
      )}

    </div>
  );
}

OperatorHome.propTypes = {
  operatorState: PropTypes.shape({
    connection: PropTypes.string.isRequired,
    indexing: PropTypes.string.isRequired,
    mode: PropTypes.string,
    previewBuild: PropTypes.string.isRequired,
    previewBrowser: PropTypes.string.isRequired,
    preview: PropTypes.string, // backward compat alias
    live: PropTypes.string.isRequired,
    integrationHealth: PropTypes.string.isRequired,
    diagnosticsSeverity: PropTypes.string.isRequired,
    publishState: PropTypes.string,
    onboarding: PropTypes.shape({
      modeSelected: PropTypes.bool,
      integrationStepCompleted: PropTypes.bool,
      previewBuilt: PropTypes.bool,
      wizardCompleted: PropTypes.bool,
    }),
    nextAction: PropTypes.shape({
      key: PropTypes.string.isRequired,
      messageKey: PropTypes.string.isRequired,
      route: PropTypes.string,
      severity: PropTypes.string.isRequired,
    }),
  }).isRequired,
  shopId: PropTypes.string,
  isLoading: PropTypes.bool,
  isServiceUpdating: PropTypes.bool,
  liveIndexing: PropTypes.shape({
    status: PropTypes.string.isRequired,
    progress: PropTypes.number.isRequired,
    activeIndexPresent: PropTypes.bool.isRequired,
    error: PropTypes.string,
    isLoading: PropTypes.bool,
    isStarting: PropTypes.bool,
  }),
  onStartIndexing: PropTypes.func,
  catalogSync: PropTypes.shape({
    status: PropTypes.string.isRequired,
    rawStatus: PropTypes.string,
    progress: PropTypes.number,
    indexedCount: PropTypes.number,
    totalCount: PropTypes.number,
    lastSuccessAt: PropTypes.string,
    queueAgeSeconds: PropTypes.number,
    isStarting: PropTypes.bool,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    startFullUpdate: PropTypes.func.isRequired,
  }),
  pluginDiagnosticsSummary: PropTypes.shape({
    highestSeverity: PropTypes.string,
    needsAttention: PropTypes.bool,
  }),
  pluginCapabilities: PropTypes.shape({
    diagnosticEventsV1: PropTypes.bool,
    operatorStateV1: PropTypes.bool,
    agentMcpAdminV1: PropTypes.bool,
  }),
  onPublish: PropTypes.func,
};

OperatorHome.defaultProps = {
  shopId: null,
  isLoading: false,
  isServiceUpdating: false,
  liveIndexing: null,
  onStartIndexing: null,
  catalogSync: null,
  pluginDiagnosticsSummary: null,
  pluginCapabilities: null,
  onPublish: null,
};
